import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ManagerService from '../../services/ManagerService';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.css';
import * as XLSX from 'xlsx';

// Đăng ký các scale và component cần thiết cho biểu đồ
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BaoCaoDoanhThuKDComponent = () => {
    const [startDay, setStartDay] = useState('');
    const [endDay, setEndDay] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [tableData, setTableData] = useState([]);   
    const [tableColumns, setTableColumns] = useState([]); 

    const isSameMonth = (startDay, endDay) => {
        const start = new Date(startDay);
        const end = new Date(endDay);
        return (
            start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
        );
    };

    const taiBaoCaoDoanhThuKD = async () => {
        try {
            await ManagerService.taiBaoCaoDoanhThuKD();
        } catch (error) {
            console.error('Lỗi khi tải báo cáo doanh thu kinh doanh:', error);
            setErrorMessage('Không thể tải báo cáo doanh thu.');
        } finally {
            setLoading(false);
        }
    };

    const handleTongHopDoanhThu = async () => {
        if (!isSameMonth(startDay, endDay)) {
            setErrorMessage('Ngày bắt đầu và ngày kết thúc phải nằm trong cùng một tháng.');
            return;
        }
        setErrorMessage('');
        setLoading(true); 
    
        try {
            await ManagerService.tongHopDoanhThuKinhDoanh(startDay, endDay);
            setTimeout(async () => {
                try {
                    await taiBaoCaoDoanhThuKD();
                } catch (downloadError) {
                    console.error('Lỗi khi tải báo cáo doanh thu kinh doanh:', downloadError);
                    setErrorMessage('Không thể tải báo cáo doanh thu.');
                } finally {
                    setLoading(false); 
                }
            }, 2 * 60 * 1000); // 5 phút
        } catch (error) {
            console.error('Lỗi khi tổng hợp doanh thu:', error);
            setErrorMessage('Lỗi khi tổng hợp doanh thu. Vui lòng thử lại.');
            setLoading(false);
        }
    };

    const handleLayDuLieuThoKD = async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await ManagerService.layDuLieuThoKD(startDay, endDay);            
            setTableColumns(response.columns);
            setTableData(response.data);
        } catch (error) {
            console.error(error);
            setErrorMessage('Không thể lấy dữ liệu thô KD.');
        } finally {
            setLoading(false);
        }
    };

    const handleLayDuLieuChuyenKDDaGan = async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await ManagerService.layDuLieuChuyenKDDaGan(startDay, endDay);
            setTableColumns(response.columns);
            setTableData(response.data);
        } catch (error) {
            console.error(error);
            setErrorMessage('Không thể lấy dữ liệu chuyến KD đã gán.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = () => {
        if (tableData.length === 0) {
            setErrorMessage('Không có dữ liệu để tải xuống.');
            return;
        }
        const ws = XLSX.utils.json_to_sheet(tableData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, 'du_lieu_chuyen_kd.xlsx');
    };

    // Hàm hiển thị nút với spinner nếu loading
    const renderButtonWithSpinner = (variant, onClick, text) => {
        return (
            <Button
                variant={variant}
                onClick={onClick}
                disabled={loading}
                size="sm" // nút nhỏ hơn
                className="w-100" // chiếm 100% chiều rộng cột
            >
                {loading ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                        />{' '}
                        Đang xử lý...
                    </>
                ) : (
                    text
                )}
            </Button>
        );
    };

    return (
        <Container fluid>
            <h1 className="my-4">Báo cáo doanh thu kinh doanh</h1>

            {errorMessage && (
                <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
                    {errorMessage}
                </Alert>
            )}

            <Form>
                <Row className="mb-3">
                    <Form.Group as={Col} controlId="formStartDay">
                        <Form.Label>Ngày bắt đầu</Form.Label>
                        <Form.Control
                            type="date"
                            value={startDay}
                            onChange={(e) => setStartDay(e.target.value)}
                            required
                            size="sm"
                        />
                    </Form.Group>

                    <Form.Group as={Col} controlId="formEndDay">
                        <Form.Label>Ngày kết thúc</Form.Label>
                        <Form.Control
                            type="date"
                            value={endDay}
                            onChange={(e) => setEndDay(e.target.value)}
                            required
                            size="sm"
                        />
                    </Form.Group>
                </Row>

                <Row className="mb-3">
                    <Col xs={6} md={3}>
                        {renderButtonWithSpinner("danger", handleTongHopDoanhThu, "Tổng hợp doanh thu")}
                    </Col>
                    <Col xs={6} md={3}>
                        {renderButtonWithSpinner("primary", handleLayDuLieuThoKD, "Lấy dữ liệu thô KD")}
                    </Col>
                    <Col xs={6} md={3}>
                        {renderButtonWithSpinner("secondary", handleLayDuLieuChuyenKDDaGan, "Lấy dữ liệu chuyến KD đã gán")}
                    </Col>
                    <Col xs={6} md={3}>
                        {renderButtonWithSpinner("success", handleDownloadExcel, "Tải dữ liệu Excel")}
                    </Col>
                </Row>
            </Form>

            {tableData.length > 0 && (
                <div style={{marginTop: '20px'}}>
                    <HotTable
                        data={tableData}
                        columns={tableColumns.map(column => ({data: column}))}
                        colHeaders={tableColumns}
                        rowHeaders={true}
                        width="100%"
                        height="600" // Tăng chiều cao bảng
                        licenseKey="non-commercial-and-evaluation"
                    />
                </div>
            )}
        </Container>
    );
};

export default BaoCaoDoanhThuKDComponent;
